import React from 'react';
import { Instagram } from 'lucide-react';
import { instagramTiles, SITE } from '../data/mock';
import useReveal from '../hooks/useReveal';
import { useSiteMedia } from '../utils/useSiteMedia';
import { fitToClass } from '../utils/mediaFit';
import { spanToGridClass } from '../utils/aspect';

const InstagramGrid = () => {
  const [headRef, headVisible] = useReveal();
  const [gridRef, gridVisible] = useReveal();

  // Admin-managed via the "home_instagram" site-media slot (any number of
  // photos/videos, reorderable). Falls back to the built-in tiles when empty.
  const slot = useSiteMedia('home_instagram');
  const adminTiles = (slot?.media || []).map((m) => ({
    src: m.url,
    alt: m.alt || '',
    kind: m.kind || 'image',
    fit: m.fit || 'cover',
    span: m.span || 'square',
  }));
  const tiles = adminTiles.length > 0
    ? adminTiles
    : instagramTiles.map((src) => ({ src, alt: '', kind: 'image', fit: 'cover', span: 'square' }));

  return (
    <section className="bg-[#EDE3CF] py-16">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div ref={headRef} className={`reveal ${headVisible ? 'in' : ''} text-center mb-12`}>
          <p className="text-[11px] tracking-[0.32em] uppercase text-[#A07A4F] mb-3">— Real homes</p>
          <h2
            className="text-4xl md:text-5xl text-[#33200F]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Tagged <span className="italic text-[#5B3A24]">#timberandthread</span>
          </h2>
          <a
            href="#"
            className="mt-5 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-[#33200F] link-underline"
          >
            <Instagram className="h-4 w-4" strokeWidth={1.5} /> @{SITE.ig}
          </a>
        </div>

        <div
          ref={gridRef}
          className={`reveal-stagger ${gridVisible ? 'in' : ''} grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 auto-rows-[42vw] sm:auto-rows-[200px] lg:auto-rows-[150px] [grid-auto-flow:dense]`}
          data-testid="instagram-grid"
        >
          {tiles.map((t, i) => {
            const fitCls = fitToClass(t.fit);
            return (
              <a
                key={`${t.src}-${i}`}
                href="#"
                className={`group relative overflow-hidden bg-[#E3D5B8] ${spanToGridClass(t.span)}`}
              >
                {t.kind === 'video' ? (
                  <video
                    src={t.src}
                    className={`w-full h-full ${fitCls} image-zoom`}
                    muted
                    loop
                    playsInline
                    autoPlay
                  />
                ) : (
                  <img
                    src={t.src}
                    alt={t.alt || `Timber & Thread Co. on Instagram — image ${i + 1}`}
                    className={`w-full h-full ${fitCls} image-zoom`}
                  />
                )}
                <div className="absolute inset-0 bg-[#1E140B]/0 group-hover:bg-[#1E140B]/40 transition-colors flex items-center justify-center">
                  <Instagram
                    className="h-7 w-7 text-[#F5EDDD] opacity-0 group-hover:opacity-100 transition-opacity"
                    strokeWidth={1.4}
                  />
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default InstagramGrid;
